import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Lock, Gamepad2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const SPORT_OPTIONS = [
  { id: 'football', emoji: '🏈', label: 'Football' },
  { id: 'soccer', emoji: '⚽', label: 'Soccer' },
  { id: 'basketball', emoji: '🏀', label: 'Basketball' },
  { id: 'baseball', emoji: '⚾', label: 'Baseball' },
];

const POSITIONS: Record<string, { id: string; label: string }[]> = {
  football: [
    { id: 'qb', label: 'Quarterback (QB)' },
    { id: 'rb', label: 'Running Back (RB)' },
    { id: 'wr', label: 'Wide Receiver (WR)' },
    { id: 'te', label: 'Tight End (TE)' },
    { id: 'ol', label: 'Offensive Line (OL)' },
    { id: 'dl', label: 'Defensive Line (DL)' },
    { id: 'lb', label: 'Linebacker (LB)' },
    { id: 'cb', label: 'Cornerback (CB)' },
    { id: 's', label: 'Safety (S)' },
    { id: 'k', label: 'Kicker / Punter' },
  ],
  soccer: [
    { id: 'gk', label: 'Goalkeeper (GK)' },
    { id: 'cb', label: 'Center Back (CB)' },
    { id: 'fb', label: 'Fullback (FB)' },
    { id: 'cdm', label: 'Defensive Mid (CDM)' },
    { id: 'cm', label: 'Central Mid (CM)' },
    { id: 'cam', label: 'Attacking Mid (CAM)' },
    { id: 'w', label: 'Winger (W)' },
    { id: 'st', label: 'Striker (ST)' },
  ],
  basketball: [
    { id: 'pg', label: 'Point Guard (PG)' },
    { id: 'sg', label: 'Shooting Guard (SG)' },
    { id: 'sf', label: 'Small Forward (SF)' },
    { id: 'pf', label: 'Power Forward (PF)' },
    { id: 'c', label: 'Center (C)' },
  ],
  baseball: [
    { id: 'p', label: 'Pitcher (P)' },
    { id: 'c', label: 'Catcher (C)' },
    { id: '1b', label: 'First Baseman (1B)' },
    { id: '2b', label: 'Second Baseman (2B)' },
    { id: '3b', label: 'Third Baseman (3B)' },
    { id: 'ss', label: 'Shortstop (SS)' },
    { id: 'lf', label: 'Left Fielder (LF)' },
    { id: 'cf', label: 'Center Fielder (CF)' },
    { id: 'rf', label: 'Right Fielder (RF)' },
  ],
};

interface AddPlayerModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    username: string;
    display_name: string;
    pin: string;
    sport: string;
    position?: string;
  }) => Promise<void>;
  currentCount: number;
}

export function AddPlayerModal({ open, onClose, onCreate, currentCount }: AddPlayerModalProps) {
  const [step, setStep] = useState<'info' | 'sport'>('info');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const [sport, setSport] = useState('football');
  const [position, setPosition] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep('info');
    setUsername('');
    setDisplayName('');
    setPin('');
    setSport('football');
    setPosition('');
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onCreate({
        username: username.trim().toLowerCase(),
        display_name: displayName.trim() || username.trim(),
        pin,
        sport,
        position: position || undefined,
      });
      toast.success(`${displayName || username} has been added! 🎉`);
      handleClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create player');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/40 backdrop-blur-sm px-4 pb-20 sm:pb-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-card rounded-2xl border-2 border-border shadow-elevated overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-display font-bold text-lg">
              {step === 'info' ? 'Add a Player' : 'Choose Sport & Position'}
            </h2>
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-5 space-y-4">
            {step === 'info' ? (
              <>
                <p className="text-sm text-muted-foreground font-body">
                  Create a player profile ({currentCount}/3 used). They'll log in with their username + PIN.
                </p>

                <div className="space-y-3">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Username (e.g. jaketheqb)"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                      maxLength={20}
                      className="pl-10 h-12 rounded-xl border-2 font-body"
                    />
                  </div>

                  <div className="relative">
                    <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Display name (optional)"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={50}
                      className="pl-10 h-12 rounded-xl border-2 font-body"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="tel"
                      placeholder="4-6 digit PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="pl-10 h-12 rounded-xl border-2 font-body tracking-[0.3em]"
                    />
                  </div>
                </div>

                <Button
                  onClick={() => {
                    if (!username.trim() || username.trim().length < 3) {
                      toast.error('Username must be at least 3 characters');
                      return;
                    }
                    if (!/^\d{4,6}$/.test(pin)) {
                      toast.error('PIN must be 4-6 digits');
                      return;
                    }
                    setStep('sport');
                  }}
                  className="w-full h-12 text-base font-display font-bold rounded-xl"
                >
                  Next: Pick Sport
                </Button>
              </>
            ) : (
              <>
                {/* Sport selection */}
                <div className="space-y-2">
                  <label className="text-sm font-display font-bold text-foreground">Sport</label>
                  <div className="grid grid-cols-3 gap-2">
                    {SPORT_OPTIONS.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => { setSport(s.id); setPosition(''); }}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          sport === s.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border hover:border-primary/30'
                        }`}
                      >
                        <span className="text-2xl block">{s.emoji}</span>
                        <span className="text-xs font-display font-bold mt-1 block">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Position selection */}
                <div className="space-y-2">
                  <label className="text-sm font-display font-bold text-foreground flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> Position (optional)
                  </label>
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className="w-full h-12 rounded-xl border-2 border-input bg-background px-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">No position selected</option>
                    {(POSITIONS[sport] || []).map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('info')}
                    className="flex-1 h-12 font-display font-bold rounded-xl border-2"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 h-12 text-base font-display font-bold rounded-xl"
                  >
                    {loading ? 'Creating...' : 'Add Player'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
